import { usePage } from "@inertiajs/react"
import type { ParseKeys } from "i18next"
import type { LucideIcon } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  PageChrome,
  useTrail,
  type Crumb,
  type PageChromeProps,
} from "@/components/page-chrome"
import type { BarePage, PageLayout } from "@/components/page-chrome/page-layout"
import { usePathParams } from "@/sharedProps"

import {
  workspaceFromPathParams,
  type Workspace,
  type WorkspaceKey,
  type WorkspaceNamespace,
  type WorkspaceOwner,
} from "./definition"

export type { WorkspaceKey, WorkspaceNamespace }

// A page with the band, whose chrome walks a fixed trail from the Workspace
// root. Its static facts are loadable without the page's code.
export type WorkspaceTrailPage<Key extends PropertyKey = string> = {
  layout: Exclude<PageLayout, "bare">
  // The workspace the page belongs to, the root of its trail: an owner type
  // and the route parameter naming the owner (WORKSPACE_OWNERS).
  owner: WorkspaceOwner
  icon: LucideIcon
  title: WorkspaceKey
  href: (pathParams: Record<string, string>) => string
  // Another page of the same registry; a page with none is a child of the
  // Workspace root.
  parent?: Key
  // The type of the record the page shows, for an ancestor crumb labelled
  // "Name (Type)".
  record?: WorkspaceKey
  // The workspace's home page: the root every trail in it starts from, so
  // its own trail is itself alone. It names no parent.
  root?: true
}

// A registry entry: a page with a trail, or a bare page (one opened in a
// sheet) that carries its layout alone.
export type WorkspacePage<Key extends PropertyKey = string> =
  | WorkspaceTrailPage<Key>
  | BarePage

export type WorkspacePages<T> = { [K in keyof T]: WorkspacePage<keyof T> }

// The pages of a registry that show a record, whose names a page passes in
// `records`.
export type WorkspaceRecordPage<T> = {
  [K in keyof T]: T[K] extends { record: WorkspaceKey } ? K : never
}[keyof T]

export type WorkspaceRecords<T> = Partial<
  Record<WorkspaceRecordPage<T>, string>
>

// A domain's registry, keyed by Inertia component name. The const type
// parameter keeps each parent as its literal, so a parent that is not a key
// of the registry fails to compile.
export function defineWorkspacePages<const T extends WorkspacePages<T>>(
  pages: T,
): T {
  return pages
}

function entryOf<T extends WorkspacePages<T>>(
  pages: T,
  page: string,
): WorkspacePage<keyof T> {
  if (!Object.hasOwn(pages, page)) {
    throw new Error(`${page} is not a registered workspace page`)
  }
  return pages[page as keyof T]
}

// The entry of a page with a trail. Raises when `page` is not registered or
// is a bare page, which has no trail to walk.
function trailEntryOf<T extends WorkspacePages<T>>(
  pages: T,
  page: string,
): WorkspaceTrailPage<keyof T> {
  const entry = entryOf(pages, page)
  if (entry.layout === "bare") {
    throw new Error(`${page} is a bare page with no trail`)
  }
  return entry
}

// The pages from the root to `page`, root first, `page` last. Raises when
// `page` is not registered or its parents loop.
export function workspacePageTrail<T extends WorkspacePages<T>>(
  pages: T,
  page: string,
): (keyof T & string)[] {
  const trail: string[] = []
  let current: string | undefined = page
  while (current !== undefined) {
    if (trail.includes(current)) {
      throw new Error(
        `Workspace page ${page} has a parent cycle: ${[current, ...trail].join(" > ")}`,
      )
    }
    const entry = trailEntryOf(pages, current)
    trail.unshift(current)
    current = entry.parent as string | undefined
  }
  return trail as (keyof T & string)[]
}

// The workspace `page` belongs to, from its owner and the page's own route.
// Raises when `page` is not registered or its route does not name its owner.
export function workspacePageWorkspace<T extends WorkspacePages<T>>(
  pages: T,
  page: string,
  pathParams: Record<string, string>,
): Workspace {
  return workspaceFromPathParams(trailEntryOf(pages, page).owner, pathParams)
}

// The workspace a page outside any registry (a workspace's home page) belongs
// to, read from its own route.
export function useRouteWorkspace(owner: WorkspaceOwner): Workspace {
  return workspaceFromPathParams(owner, usePathParams())
}

const namespaceOf = (key: WorkspaceKey) =>
  key.slice(0, key.indexOf(":")) as WorkspaceNamespace

// Every namespace the trail to `page` labels its crumbs and title from, in
// trail order.
export function workspacePageNamespaces<T extends WorkspacePages<T>>(
  pages: T,
  page: string,
): WorkspaceNamespace[] {
  const keys = workspacePageTrail(pages, page).flatMap((name) => {
    const { title, record } = trailEntryOf(pages, name)
    return record === undefined ? [title] : [title, record]
  })
  return [...new Set(keys.map(namespaceOf))]
}

// The crumbs from the registry's root to the parent of `page`, without the
// Workspace root. A record ancestor carries the record's name and its type,
// which the chrome reads as "Name (Type)", and raises when the page passes no
// name for it.
export function workspacePageCrumbs<T extends WorkspacePages<T>>(
  pages: T,
  page: string,
  {
    pathParams,
    records,
    t,
  }: {
    pathParams: Record<string, string>
    records: WorkspaceRecords<T>
    t: (key: WorkspaceKey) => string
  },
): Crumb[] {
  return workspacePageTrail(pages, page)
    .slice(0, -1)
    .map((name) => {
      const { title, record, href } = trailEntryOf(pages, name)
      if (record === undefined) {
        return { label: t(title), href: href(pathParams) }
      }

      const recordName = (records as Partial<Record<string, string>>)[name]
      if (recordName === undefined) {
        throw new Error(
          `Workspace page ${page} needs a record name for ${name}`,
        )
      }
      return { label: recordName, type: t(record), href: href(pathParams) }
    })
}

export type WorkspacePageChromeProps<T> = Omit<
  PageChromeProps,
  "icon" | "title" | "crumbs"
> & {
  records?: WorkspaceRecords<T>
}

// Binds a domain's registry to a chrome component. The page is found by its
// Inertia component name; its trail starts at the Workspace root, then
// walks the registry from the root to the page's parent. A workspace's home
// page is the root itself and gets no crumbs. Everything else, identity,
// secondary and action included, passes through to PageChrome.
export function workspacePageChrome<T extends WorkspacePages<T>>(pages: T) {
  return function WorkspacePageChrome({
    records = {},
    ...chrome
  }: WorkspacePageChromeProps<T>) {
    const page = usePage().component
    const { t } = useTranslation(workspacePageNamespaces(pages, page))
    const translate = (key: WorkspaceKey) => t(key as ParseKeys)
    const pathParams = usePathParams()
    const trail = useTrail(
      workspacePageWorkspace(pages, page, pathParams),
      ...workspacePageCrumbs(pages, page, {
        pathParams,
        records,
        t: translate,
      }),
    )
    const { icon, title, root } = trailEntryOf(pages, page)

    return (
      <PageChrome
        {...chrome}
        icon={icon}
        title={translate(title)}
        crumbs={root ? [] : trail}
      />
    )
  }
}
